import { trackSwapRandomTestBuyArticle } from "@coworker/app/src/helpers/tracker";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans, useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import { replaceTestbuyArticle } from "../../services/testbuy.service";
import { Testbuy } from "@coworker/apprestructured/src/testbuy/services/testbuy.service";
import { replaceTestbuyArticleAndRefetch } from "../../services/queryKeyHelper";
import { TestBuyProductWithCriteria } from "@coworker/types/lib/testbuy";
import { useState } from "react";
import { ChangeArticleSheet } from "../ChangeArticleSheet/ChangeArticleSheet";
import { TestbuyVariantSearch } from "../TestbuyVariantSearch/TestbuyVariantSearch";
import { ScannerErrorComponent } from "@ingka-group-digital/flexa-scanner";
import { ProductVariant } from "@coworker/apprestructured/src/shared/hooks/item/useItemVariants";

interface ChangeArticleButtonProps {
  testbuy: Testbuy;
  article: TestBuyProductWithCriteria;
  area: string;
  onLoadingChange?: (loading: boolean) => void;
}

export const ChangeArticleButton = ({
  testbuy,
  article,
  area,
}: ChangeArticleButtonProps) => {
  // Hooks
  const store = useMyStore();
  const { showToast } = useToastNotification();
  const { t } = useTranslation();
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    store?.primaryLocale
  );
  const queryClient = useQueryClient();
  const [isChangeArticleOpen, setIsChangeArticleOpen] = useState(false);
  const [isVariantSearchOpen, setIsVariantSearchOpen] = useState(false);
  const [productVariants, setProductVariants] = useState<ProductVariant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const replaceArticleMutation = useMutation({
    mutationFn: async (newArticleShortId: string) => {
      if (!article.id) return null;
      return replaceTestbuyArticle(testbuy.id, article.id, {
        locale: userLanguage,
        countryId: store?.countryId ?? "",
        articleShortId: newArticleShortId,
      });
    },
    onSuccess: async (newArticle) => {
      if (newArticle) {
        await replaceTestbuyArticleAndRefetch(
          queryClient,
          testbuy.id,
          article.shortId,
          newArticle.shortId
        );
      }
      setIsVariantSearchOpen(false);
    },
  });

  // Event handlers
  const handleGetNewArticle = async () => {
    trackSwapRandomTestBuyArticle(testbuy.strategy, article.shortId, area);
    setIsChangeArticleOpen(true);
  };

  const handleApplyVariant = async (itemNo: string) => {
    if (testbuy.articles.some(({ shortId }) => shortId === itemNo)) {
      setError("ARTICLE_ALREADY_EXISTS");
      setIsVariantSearchOpen(false);
      return;
    }

    try {
      await replaceArticleMutation.mutateAsync(itemNo);
    } catch {
      showToast(
        <Trans>couldNotUseThisArticleString</Trans>,
        undefined,
        undefined,
        5
      );
    }
  };

  if (!article.id) return null;
  return (
    <>
      <FixaButton
        type="secondary"
        size="xsmall"
        onClick={handleGetNewArticle}
        disabled={replaceArticleMutation.isPending}
        text={t("changeArticleString")}
      />
      <ChangeArticleSheet
        visible={isChangeArticleOpen}
        onClose={() => setIsChangeArticleOpen(false)}
        onOpenVariant={(variants) => {
          setProductVariants(variants);
          setIsChangeArticleOpen(false);
          setIsVariantSearchOpen(true);
        }}
        article={article}
        testbuy={testbuy}
        area={area}
      />
      <ScannerErrorComponent error={error} setError={setError} />
      <TestbuyVariantSearch
        key={productVariants
          .map(({ color, size }) => `${color}-${size}`)
          .join("|")}
        isSearching={isVariantSearchOpen}
        setIsSearching={setIsVariantSearchOpen}
        productVariants={productVariants}
        onClose={() => setIsChangeArticleOpen(true)}
        onApply={(itemNo) => void handleApplyVariant(itemNo)}
        isApplying={replaceArticleMutation.isPending}
      />
    </>
  );
};
